import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { CalendarDays, Clock, User, CheckCircle2, ArrowRight, ArrowLeft, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const TIMES = ["10:00 AM", "11:00 AM", "12:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"];

function next14Days() {
  const days = [];
  const now = new Date();
  for (let i = 1; i <= 14; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push(d);
  }
  return days;
}

const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: "short" });
const fmtDate = (d) => d.toLocaleDateString(undefined, { day: "2-digit" });
const fmtMonth = (d) => d.toLocaleDateString(undefined, { month: "short" });
const fmtFull = (d) => d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function BookingModal({ open, onOpenChange }) {
  const { toast } = useToast();
  const days = useMemo(() => next14Days(), []);
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setDate(null);
        setTime(null);
        setForm({ name: "", email: "", phone: "", topic: "" });
        setDone(false);
      }, 300);
    }
  }, [open]);

  const canNext =
    (step === 1 && date) ||
    (step === 2 && time) ||
    (step === 3 && form.name && form.email);

  const submit = async () => {
  try {
    const booking = {
      date: date.toISOString(),
      time,
      ...form,
    };

    const response = await fetch("/api/bookings.php",{
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(booking),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "Something went wrong.");
    }

    setDone(true);

    toast({
      title: "Call booked!",
      description: `${fmtFull(date)} at ${time}.`,
    });

  } catch (error) {
    console.error("Booking error:", error);

    toast({
      title: "Something went wrong",
      description: "We couldn't submit your enquiry. Please try again.",
      variant: "destructive",
    });
  }
};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-lenis-prevent className="p-0 w-[calc(100%-2rem)] max-w-2xl max-h-[92vh] overflow-y-auto rounded-[24px] border-black/10 [&>button.absolute]:hidden">
        <DialogTitle className="sr-only">Book a discovery call</DialogTitle>

        {done ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#B7F0BE] mx-auto flex items-center justify-center">
              <CheckCircle2 size={30} className="text-[#0A5C36]" />
            </div>
            <h3 className="display text-3xl mt-5">You&apos;re all set!</h3>
            <p className="text-neutral-600 mt-2">
              Your call is booked for
              <br />
              <b className="text-black">{fmtFull(date)}</b> at <b className="text-black">{time}</b>
            </p>
            <p className="text-sm text-neutral-500 mt-4">
              I&apos;ll email <b>{form.email}</b> with the meeting link within 5 minutes.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-8 h-12 px-8 rounded-full bg-black text-white text-sm font-semibold hover:bg-neutral-800 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div className="p-6 md:p-8 pb-4 border-b border-black/5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-neutral-500">
                    Free · 20 minutes
                  </p>
                  <h3 className="display text-2xl md:text-3xl mt-1">
                    Book a discovery call
                  </h3>
                </div>
                <button
                  onClick={() => onOpenChange(false)}
                  aria-label="close"
                  className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-neutral-100"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Steps */}
              <div className="flex items-center gap-2 mt-5">
                {[
                  { n: 1, label: "Date", Icon: CalendarDays },
                  { n: 2, label: "Time", Icon: Clock },
                  { n: 3, label: "You", Icon: User },
                ].map((s, i) => (
                  <div key={s.n} className="flex items-center gap-2 flex-1">
                    <div
                      className={`h-8 pl-2 pr-3 rounded-full flex items-center gap-1.5 text-xs font-medium transition-colors ${
                        step >= s.n
                          ? "bg-black text-white"
                          : "bg-neutral-100 text-neutral-500"
                      }`}
                    >
                      <s.Icon size={13} />
                      {s.label}
                    </div>
                    {i < 2 && (
                      <div
                        className={`h-0.5 flex-1 rounded-full ${
                          step > s.n ? "bg-black" : "bg-neutral-200"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8 min-h-[300px]">
              {step === 1 && (
                <div>
                  <p className="text-sm text-neutral-600 mb-4">Pick a date that works for you.</p>
                  <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {days.map((d) => {
                      const active = date && d.toDateString() === date.toDateString();
                      return (
                        <button
                          key={d.toISOString()}
                          onClick={() => setDate(d)}
                          className={`p-3 rounded-2xl border-2 text-center transition-all ${
                            active
                              ? "bg-black text-white border-black"
                              : "bg-white border-black/10 hover:border-black"
                          }`}
                        >
                          <div className="text-[10px] uppercase tracking-wider opacity-70">
                            {fmtDay(d)}
                          </div>
                          <div className="display text-xl font-extrabold mt-0.5">
                            {fmtDate(d)}
                          </div>
                          <div className="text-[10px] uppercase opacity-70">
                            {fmtMonth(d)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <p className="text-sm text-neutral-600 mb-4">
                    Pick a slot on <b className="text-black">{fmtFull(date)}</b> (IST).
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {TIMES.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTime(t)}
                        className={`h-12 rounded-2xl border-2 text-sm font-semibold transition-all ${
                          time === t
                            ? "bg-black text-white border-black"
                            : "bg-white border-black/10 hover:border-black"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <p className="text-sm text-neutral-600">
                    Just a few details so I can send the meeting link.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">
                        Full name *
                      </label>
                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Jane Doe"
                        className="mt-2 w-full h-11 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="jane@brand.com"
                        className="mt-2 w-full h-11 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">
                        Phone (optional)
                      </label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 99999 99999"
                        className="mt-2 w-full h-11 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-neutral-500">
                        What&apos;s it about?
                      </label>
                      <input
                        value={form.topic}
                        onChange={(e) => setForm({ ...form, topic: e.target.value })}
                        placeholder="New Shopify store"
                        className="mt-2 w-full h-11 border-b-2 border-black/10 focus:border-black outline-none bg-transparent text-[15px] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-4 rounded-2xl bg-neutral-50 flex items-center gap-3 text-sm">
                    <CalendarDays size={16} className="text-neutral-500 shrink-0" />
                    <span>
                      <b className="text-black">{fmtFull(date)}</b> at{" "}
                      <b className="text-black">{time}</b> · 20 min · Google Meet
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 md:p-8 pt-4 border-t border-black/5 flex items-center justify-between">
              <button
                onClick={() => setStep(step - 1)}
                disabled={step === 1}
                className="h-11 px-4 rounded-full text-sm font-medium text-neutral-600 hover:text-black disabled:opacity-0 flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={() => (step === 3 ? submit() : setStep(step + 1))}
                disabled={!canNext}
                className="h-11 pl-5 pr-1.5 rounded-full bg-black text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors group"
              >
                {step === 3 ? "Confirm booking" : "Continue"}
                <span className="w-8 h-8 rounded-full bg-[#FFE14D] text-black flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight size={14} />
                </span>
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
